import { Component, TemplateFunction } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { scrollTo, waitForProp } from '@ribajs/utils'
import {
  ShopifyProductService,
  AnalyticsService,
  ShopifyCollectionService,
  ShopifyCartService,
} from '../services/index.js'
import { SbTabsComponent } from './index.js'
import { SbVariantSelectorComponent } from './sb-variant-selector.component.js'

import type {
  ShopifyProductVariant,
  LineItemProperties,
  ScopeSbProduct,
  ShopifyProduct,
} from '../types/index.js'

const AUTO_UPDATE_INTERVAL_IN_SECONDS = 600

/**
 * Hardcoded labels of product option "Inhalt" for identifying it in various languages.
 * See ScopeSbProduct.showPackageContentsFallback for further information.
 */
const LOCALIZED_PACKAGE_CONTENTS_LABELS: Record<string, string | undefined> = {
  DE: 'Inhalt',
  FR: 'Contenu',
  INT: 'Content', // TODO: check if this is correct
}

export class SbProductComponent extends Component {
  public static tagName = 'sb-product'

  protected autoUpdateTimer: number | null = null

  /** The child `sb-variant-selector` to select the variant */
  public childVariantSelector: SbVariantSelectorComponent | null = null

  /**
   * - handle is the product handle to get the product json object
   * - product is the product object itself if you want to pass it directly
   */
  static get observedAttributes(): string[] {
    return [
      'handle',
      'variant-id',
      'product',
      'track-view',
      'collection-handle',
      'is-recommendation',
      'is-favorite',
      'quantity',
      'order-quantity-limit',
      'allow-trusted-shop-widgets',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['handle']
  }

  public scope: ScopeSbProduct = {
    handle: undefined,
    collectionHandle: undefined,
    isRecommendation: false,
    isFavorite: false,
    isFreePresent: false,
    product: undefined,
    variant: undefined,
    featured_image: {},
    options: [],
    quantity: 1,
    orderQuantityLimit: -1,
    quantityMinimumUndercut: false,
    quantityLimitExceeded: false,
    metafields: null,
    colliLabel: '',
    decrease: this.decrease,
    increase: this.increase,
    goToRating: this.goToRating,

    badges: {
      new: false,
      sale: 0,
      saleSum: 0,
      soldOut: false,
      outOfStockText: '',
      discountInfo: '',
      additionalInfo: '',
    },

    trackView: true,
    allowTrustedShopWidgets: true,

    ratingTemplate: '',
    ratingSmallTemplate: '',

    showPackageContentsFallback: this.showPackageContentsFallback,
  }

  protected events = EventDispatcher.getInstance()

  constructor() {
    super()
    this.onVariantChanged = this.onVariantChanged.bind(this)
  }

  /**
   * Prepare product, remove protocol from featured_image, lowercase the option names
   * @param product product object
   */
  protected async onProductChanged() {
    this.scope.handle = this.scope.handle || this.scope.product?.handle
  }

  protected async updateProductData(enableCache: boolean) {
    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }
    const {
      product,
      metafields,
      badges,
      orderQuantityLimit,
      bgColor,
      featuredImage,
      // variant,
    } = await ShopifyProductService.getPreparedProductData(
      this.scope.handle,
      this.scope.variantId,
      enableCache,
    )
    this.scope.product = product
    this.scope.metafields = metafields
    this.scope.badges = badges
    this.scope.orderQuantityLimit = orderQuantityLimit
    this.scope.featured_image = featuredImage

    this.onProductChanged()

    ShopifyProductService.setImageBgColor(this, bgColor)
  }

  protected updateRatingTemplates() {
    const oldBarcode = this.scope.currentBarcode
    const newBarcode = this.scope.variant?.barcode
    const barcodeChanged = oldBarcode !== newBarcode

    if (!barcodeChanged) {
      return
    }

    this.scope.currentBarcode = this.scope.variant?.barcode

    // Only reload the rating template if a barcode is set
    if (this.scope.variant?.barcode) {
      // This is a WORKAROUND for the `etrusted-widget` to force template reloading
      const ratingTemplates = ShopifyProductService.getRatingTemplates(
        'text-base font-body',
        this.scope.currentBarcode,
      )

      // Set to empty string to force an template update
      if (this.scope.ratingTemplate.length > 0) this.scope.ratingTemplate = ''
      this.scope.ratingTemplate = ratingTemplates.normal

      // Set to empty string to force an template update
      if (this.scope.ratingSmallTemplate.length > 0)
        this.scope.ratingSmallTemplate = ''
      this.scope.ratingSmallTemplate = ratingTemplates.small
    } else {
      if (this.scope.ratingTemplate.length > 0) this.scope.ratingTemplate = ''
      if (this.scope.ratingSmallTemplate.length > 0)
        this.scope.ratingSmallTemplate = ''
    }
  }

  /**
   * Check whether to show the separate section "Inhalt". This is used for products without an "Inhalt" option that have it set as metafield.
   */
  showPackageContentsFallback(
    variant?: ShopifyProductVariant,
    product?: ShopifyProduct,
  ): boolean {
    if (!variant?.metafields?.seeberger.package_contents) {
      return false
    }

    const optionLabel =
      LOCALIZED_PACKAGE_CONTENTS_LABELS[window.Shopify?.country || '']
    if (!optionLabel) {
      return false
    }

    if (product?.options.some(option => option.name === optionLabel)) {
      return false
    }

    return true
  }

  protected async connectedCallback() {
    super.connectedCallback()
    this.init(SbProductComponent.observedAttributes)
  }

  // deconstruction
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
    this.stopAutoUpdater()
  }

  /**
   * Additional line item cart properties, used for tracking
   * @returns
   */
  protected getProperties() {
    const cartProperties: LineItemProperties = {}
    if (this.scope.collectionHandle) {
      cartProperties._collection = this.scope.collectionHandle
    }
    if (this.scope.parentCollectionHandle) {
      cartProperties._parentCollection = this.scope.parentCollectionHandle
    }

    cartProperties._isRecommendation = this.scope.isRecommendation
    cartProperties._isFavorite = this.scope.isFavorite
    cartProperties._isFreePresent = this.scope.isFreePresent
    cartProperties._orderQuantityLimit = this.scope.orderQuantityLimit

    return cartProperties
  }

  /**
   * Increase the product quantity the user can add to the card
   */
  public increase() {
    this.scope.quantity++
    this.validateQuantity()
  }

  /**
   * Decrease the product quantity the user can add to the card
   */
  public decrease() {
    this.scope.quantity--
    this.validateQuantity()
  }

  public validateQuantity() {
    if (this.scope.quantity <= 0) {
      this.scope.quantity = 1
      this.scope.quantityMinimumUndercut = true
    } else {
      this.scope.quantityMinimumUndercut = false
    }
    if (!this.scope.variant?.id) {
      console.error("Can't validate quantity without a selected variant")
      return
    }
    const currentCartItemQuantity =
      ShopifyCartService.getLineItem(this.scope.variant?.id)?.quantity || 0
    const validateRes = ShopifyCartService.validateQuantity(
      this.scope.quantity,
      currentCartItemQuantity,
      this.scope.orderQuantityLimit,
    )
    this.scope.quantity = validateRes.quantity
    this.scope.quantityLimitExceeded = validateRes.exceeded || false
  }

  public goToRating() {
    const tabsEl = this.querySelector<SbTabsComponent>(SbTabsComponent.tagName)

    if (!tabsEl) {
      console.error(`Element with tag ${SbTabsComponent.tagName} not found!`)
      return
    }

    tabsEl?.showByName('reviews')
    scrollTo(tabsEl, 100)
  }

  /**
   * Updates the product data without cache to ensure that the product is up to date
   * Called in regular to keep the product up-to-date
   */
  protected async updateData() {
    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    // Make sure to restore the previously selected variant
    if (this.scope.variant?.id) {
      this.scope.variantId = this.scope.variant?.id
    }

    await this.updateProductData(false)
  }

  /** Init the product data auto updater */
  protected initAutoUpdater() {
    this.autoUpdateTimer = setInterval(() => {
      this.updateData()
    }, AUTO_UPDATE_INTERVAL_IN_SECONDS * 1000)
  }

  protected stopAutoUpdater() {
    if (this.autoUpdateTimer !== null) {
      clearInterval(this.autoUpdateTimer)
    }
  }

  protected async beforeBind() {
    await super.beforeBind()

    if (!this.scope.handle && !this.scope.product) {
      throw new Error('Product handle or product not set!')
    }

    // Use the cache the first time
    await this.updateProductData(true)

    this.setElements()
    await this.addEventListeners()
  }

  protected async afterBind() {
    await super.afterBind()

    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    if (this.scope.collectionHandle) {
      this.scope.parentCollectionHandle = ShopifyCollectionService.getParent(
        this.scope.collectionHandle,
      )
    }

    this.initAutoUpdater()

    this.classList.remove('opacity-0')
    this.classList.add(`${SbProductComponent.tagName}-ready`, 'opacity-100')

    await this.trackPdpView()
  }

  protected async trackPdpView() {
    await waitForProp('currency', window.Shopify, 10000)
    if (!window.Shopify?.currency) {
      console.warn('window.Shopify.currency is not defined!')
      return
    }

    if (
      this.scope.trackView &&
      this.scope.product &&
      this.scope.variant &&
      window.Shopify.currency
    )
      AnalyticsService.getInstance()?.trackPdpView(
        this.scope.product,
        this.scope.variant,
        window.Shopify?.currency.active,
        this.scope.quantity,
        this.getProperties(),
      )
  }

  protected setElements() {
    this.setChildVariantSelector()
  }

  protected setChildVariantSelector() {
    if (!this.childVariantSelector) {
      this.childVariantSelector = this.querySelector(
        SbVariantSelectorComponent.tagName,
      )
    }
  }

  protected async addEventListeners() {
    this.childVariantSelector?.addEventListener(
      'change',
      this.onVariantChanged as EventListener,
    )
  }

  protected removeEventListeners() {
    this.childVariantSelector?.removeEventListener(
      'change',
      this.onVariantChanged as EventListener,
    )
  }

  /** Called if the variant was changed using SbVariantSelectorComponent */
  protected onVariantChanged() {
    this.updateRatingTemplates()

    // Reset quantity to 1
    this.scope.quantity = 1
    this.validateQuantity()

    const { badges, featuredImage } =
      ShopifyProductService.getPreparedProductVariantData(
        this.scope.product!,
        this.scope.variant!,
        this.scope.metafields,
      )
    this.scope.badges = badges
    this.scope.featured_image = featuredImage

    this.scope.colliLabel =
      this.scope.variant?.metafields?.seeberger?.colli_label || ''
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
